import { fromBase64 } from '@cosmjs/encoding'
import { StargateClient } from '@cosmjs/stargate'
import { Any } from 'cosmjs-types/google/protobuf/any'

import { ErrorHandler } from '@/helpers/error-handler'

import { ChainInfo, Keplr, type KeplrClient } from './types'

export function getKeplr(): Keplr | null {
  if (typeof window === 'undefined') return null
  return window.keplr ?? null
}

export async function suggestAndEnableKeplr(chain: ChainInfo) {
  const keplr = getKeplr()
  if (!keplr) throw new Error('Keplr not found')
  try {
    await keplr.experimentalSuggestChain(chain)
  } catch (error) {
    ErrorHandler.process(error, 'Failed to suggest chain to Keplr')
  }
  await keplr.enable(chain.chainId)
}

export async function getKelprClient(rpcUrl: string) {
  return StargateClient.connect(rpcUrl)
}

export async function getBalance(
  client: KeplrClient,
  address: string,
  denom: string,
) {
  const coin = await client.getBalance(address, denom)
  return coin?.amount ?? '0'
}

export function toAtomic(amount: string, decimals: number): string {
  const [integer, fraction = ''] = amount.trim().split('.')

  const paddedFraction = (fraction + '0'.repeat(decimals)).slice(0, decimals)

  const result = `${integer || '0'}${paddedFraction}`

  return result.replace(/^0+(?=\d)/, '') || '0'
}

export function encodeEthermintPubKey(rawPubKey: Uint8Array): Uint8Array {
  const keyBytes = rawPubKey
  return Uint8Array.from([0x0a, keyBytes.length, ...keyBytes])
}

export function encodeEd25519PubKey(rawPubKey: Uint8Array): Uint8Array {
  const keyBytes = rawPubKey
  return Uint8Array.from([0x0a, keyBytes.length, ...keyBytes])
}

export type ValidatorPubKeyType = 'ed25519' | 'secp256k1' | 'ethsecp256k1'

const PUBKEY_TYPEURL_MAP: Record<ValidatorPubKeyType, string> = {
  ed25519: '/cosmos.crypto.ed25519.PubKey',
  secp256k1: '/cosmos.crypto.secp256k1.PubKey',
  ethsecp256k1: '/ethermint.crypto.v1.ethsecp256k1.PubKey',
}

export function pubkeyAnyFromBase64(
  type: ValidatorPubKeyType,
  base64: string,
): Any {
  const raw = fromBase64(base64)
  const value =
    type === 'ed25519' ? encodeEd25519PubKey(raw) : encodeEthermintPubKey(raw)

  return Any.fromPartial({ typeUrl: PUBKEY_TYPEURL_MAP[type], value })
}
